import { useNavigate } from "@tanstack/react-router";
import { Route } from "@web/routes";
/**
 * Hook for managing tag based navigation in the URL search params.
 * Provides functions to add, remove, or toggle tags in the current route.
 *
 * - add: Adds a tag if not present
 * - remove: Removes a tag if present, if no tags are left, set tags to undefined
 * - toggle: Toggles a tag's presence
 * - only: Adds a tag and remove all others
 * - clear: Clears all tags
 */
export function useTagNavigation() {
  const nav = useNavigate({ from: Route.fullPath });

  const add = (tag: string) => {
    nav({
      search: (prev) => {
        const currentTags = prev.tags || [];
        if (!currentTags.includes(tag)) {
          return { ...prev, searchText: undefined, page: 1, tags: [...currentTags, tag] };
        }
        return { ...prev, page: 1 };
      }
    });
  };

  const remove = (tag: string) => {
    nav({
      search: (prev) => {
        const currentTags = prev.tags || [];
        const newTags = currentTags.filter((t) => t !== tag);
        if (newTags.length === 0) return { ...prev, searchText: undefined, page: 1, tags: undefined };
        return { ...prev, page: 1, tags: newTags };
      }
    });
  };

  const toggle = (tag: string) => {
    nav({
      search: (prev) => {
        const currentTags = prev.tags || [];
        if (currentTags.includes(tag)) {
          return { ...prev, searchText: undefined, page: 1, tags: currentTags.filter((t) => t !== tag) };
        }
        return { ...prev, searchText: undefined, page: 1, tags: [...currentTags, tag] };
      }
    });
  };

  const clear = () => {
    nav({
      search: (prev) => {
        return { ...prev, searchText: undefined, page: 1, tags: [] };
      }
    });
  };

  const only = (tag: string) => {
    nav({
      search: (prev) => {
        return { ...prev, searchText: undefined, page: 1, tags: [tag] };
      }
    });
  };

  return { add, remove, toggle, clear, only };
}
